import { useState, useEffect } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Upload, Download, Loader2, FolderKanban, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const enconOrange = '#ee7203';

const ProjectsDatabaseManager = () => {
  const [projectCount, setProjectCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get(`${API}/projects?page=1&page_size=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectCount(response.data.total || 0);
      setLastUpdate(response.data.last_update);
    } catch (error) {
      console.error('Error fetching project stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`${API}/projects/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(`Successfully imported ${response.data.imported_count} projects`);
      fetchStats();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import projects');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API}/projects/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `projects_database_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Projects database downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download projects database');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: enconOrange }}>
            <FolderKanban className="w-6 h-6" />
            Projects Database
          </CardTitle>
          <CardDescription>
            Import and export project data. Upload a CSV file to update the projects database.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold" style={{ color: enconOrange }}>
                {loading ? <Loader2 className="w-8 h-8 mx-auto animate-spin" /> : projectCount.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-1">Total Projects</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-sm font-medium text-gray-700">{formatDate(lastUpdate)}</p>
              <p className="text-sm text-gray-500 mt-1">Last Import</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Upload Button */}
            <label className="flex-1">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full rounded-xl h-14 flex items-center justify-center gap-2 cursor-pointer"
                style={{ borderColor: enconOrange, color: enconOrange }}
                disabled={uploading}
                asChild
              >
                <span>
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5" />
                  )}
                  {uploading ? 'Uploading...' : 'Import Projects CSV'}
                </span>
              </Button>
            </label>

            {/* Download Button */}
            <Button
              variant="outline"
              className="flex-1 rounded-xl h-14 flex items-center justify-center gap-2"
              style={{ borderColor: enconOrange, color: enconOrange }}
              onClick={handleDownload}
              disabled={downloading || projectCount === 0}
            >
              {downloading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              {downloading ? 'Downloading...' : 'Download Projects CSV'}
            </Button>
          </div>

          {/* Info */}
          <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
            <p className="font-medium mb-2">CSV Import Notes:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Projects are matched by Project Name + Account Name</li>
              <li>Existing projects with matching names will be updated</li>
              <li>New projects will be added automatically</li>
              <li>Supported columns: Barbour Project Name, Account Name, Value, Start Date, End Date, etc.</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectsDatabaseManager;
